import { useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'

interface FormState {
  firstName: string
  lastName: string
  email: string
  affiliation: string
  website: string
}

interface FormErrors {
  firstName: string
  lastName: string
  email: string
  affiliation: string
  submit?: string
}

const EMPTY: FormState = {
  firstName: '', lastName: '', email: '', affiliation: '', website: '',
}
const NO_ERRORS: FormErrors = {
  firstName: '', lastName: '', email: '', affiliation: '',
}

interface Props {
  isOpen: boolean
  onClose: () => void
  product: string
  productLabel: string
  fileUrl: string
  fileName: string
}

export function DatasheetDownloadModal({
  isOpen, onClose, product, productLabel, fileUrl, fileName,
}: Props) {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<FormState>(EMPTY)
  const [formErrors, setFormErrors] = useState<FormErrors>(NO_ERRORS)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    const orig = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = orig }
  }, [isOpen])

  const close = () => {
    onClose()
    setFormData(EMPTY)
    setFormErrors(NO_ERRORS)
    setIsSubmitting(false)
  }

  const validate = (): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    const emailValue = formData.email.trim()
    const errors: FormErrors = {
      firstName: formData.firstName.trim() ? '' : t('cathodicProtection.requiredFieldError'),
      lastName: formData.lastName.trim() ? '' : t('cathodicProtection.requiredFieldError'),
      email: !emailValue
        ? t('cathodicProtection.requiredFieldError')
        : !emailRegex.test(emailValue)
          ? t('contact.errors.emailInvalid')
          : '',
      affiliation: formData.affiliation.trim() ? '' : t('cathodicProtection.requiredFieldError'),
    }
    setFormErrors(errors)
    return !errors.firstName && !errors.lastName && !errors.email && !errors.affiliation
  }

  const triggerDownload = () => {
    const link = document.createElement('a')
    link.href = fileUrl
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setIsSubmitting(true)
    setFormErrors((prev) => ({ ...prev, submit: undefined }))

    try {
      const res = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'datasheet',
          product,
          productLabel,
          fileName,
          ...formData,
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        const key = data?.error === 'rate_limited'
          ? 'cathodicProtection.rateLimited'
          : 'cathodicProtection.submitError'
        setFormErrors((prev) => ({ ...prev, submit: t(key) }))
        setIsSubmitting(false)
        return
      }
      triggerDownload()
      close()
    } catch {
      setFormErrors((prev) => ({ ...prev, submit: t('cathodicProtection.submitError') }))
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,31,66,0.85)' }}
      onClick={close}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl bg-white p-6 sm:p-8 shadow-2xl"
      >
        <button
          onClick={close}
          className="absolute right-4 top-4 rounded-full p-1 text-secondary-400 transition-colors hover:bg-secondary-100 hover:text-secondary-600"
          aria-label={t('aria.close')}
        >
          <X className="h-5 w-5" />
        </button>

        <h3 className="text-xl font-bold text-secondary-900 sm:text-2xl" style={{ marginBottom: '0.5rem' }}>
          {t('cathodicProtection.downloadFormTitle')}
        </h3>
        <p className="mb-6 text-sm text-secondary-500 sm:text-base">
          {t('cathodicProtection.downloadFormSubtitle')}
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
          <input
            type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
            value={formData.website}
            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
          />

          <Input
            label={t('cathodicProtection.firstName')}
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            error={formErrors.firstName}
            showErrorMessage={false}
            aria-required="true"
            disabled={isSubmitting}
          />
          <Input
            label={t('cathodicProtection.lastName')}
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            error={formErrors.lastName}
            showErrorMessage={false}
            aria-required="true"
            disabled={isSubmitting}
          />
          <Input
            label={t('cathodicProtection.email')}
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            onFocus={() => setFormErrors((prev) => ({ ...prev, email: '' }))}
            error={formErrors.email}
            showErrorMessage={formErrors.email === t('contact.errors.emailInvalid')}
            aria-required="true"
            disabled={isSubmitting}
          />
          <Input
            label={t('cathodicProtection.affiliation')}
            value={formData.affiliation}
            onChange={(e) => setFormData({ ...formData, affiliation: e.target.value })}
            error={formErrors.affiliation}
            showErrorMessage={false}
            aria-required="true"
            disabled={isSubmitting}
          />

          {formErrors.submit && (
            <p className="text-sm text-error" role="alert">{formErrors.submit}</p>
          )}

          <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button type="button" variant="ghost" onClick={close} disabled={isSubmitting} className="order-2 sm:order-1">
              {t('aria.close')}
            </Button>
            <Button type="submit" variant="primary" isLoading={isSubmitting} className="order-1 sm:order-2">
              {t('cathodicProtection.downloadNow')}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}
